import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store';
import {
    initializeAuth,
    selectAuthStatus,
    selectIsImpersonating,
    selectIsSuperAdmin,
} from '@/store/slices/authSlice';
import { AUTH_STATUS } from '@/features/auth/constants';
import { Spinner } from '@shopify/polaris';
import { setAuthToken, setStoreUrl } from '@/utils/auth';

/**
 * GuestGuard: wraps public auth routes (/login, /register, etc.)
 *
 * If user is already authenticated → redirects to /
 * If not authenticated → renders children (login/register/etc.)
 * If initializing → shows spinner until resolved
 *
 * EXCEPTION: If the URL contains a ?token= parameter (e.g. Shopify redirect),
 * we clear the old session first so the new auth context can take over.
 */
const GuestGuard: React.FC = () => {
    const dispatch = useAppDispatch();
    const status = useAppSelector(selectAuthStatus);
    const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
    const isImpersonating = useAppSelector(selectIsImpersonating);
    const location = useLocation();
    const [tokenHandled, setTokenHandled] = useState(false);

    // Check if URL has a token param (Shopify redirect scenario)
    const searchParams = new URLSearchParams(location.search);
    const hasIncomingToken = searchParams.has('token');

    // Save incoming token to cookies and re-initialize auth
    useEffect(() => {
        if (hasIncomingToken && !tokenHandled) {
            const token = searchParams.get('token');
            const storeUrl = searchParams.get('storeUrl');
            if (token) setAuthToken(token);
            if (storeUrl) setStoreUrl(storeUrl);
            window.history.replaceState({}, '', window.location.pathname);
            setTokenHandled(true);
            dispatch(initializeAuth());
        }
    }, [hasIncomingToken, tokenHandled, dispatch]);

    useEffect(() => {
        // Only auto-init if there's no incoming token (token handler does its own init)
        if (!hasIncomingToken && status === AUTH_STATUS.INITIALIZING) {
            dispatch(initializeAuth());
        }
    }, [dispatch, status, hasIncomingToken]);

    if (status === AUTH_STATUS.INITIALIZING || (hasIncomingToken && !tokenHandled)) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Spinner />
            </div>
        );
    }

    if (status === AUTH_STATUS.AUTHENTICATED || status === AUTH_STATUS.MIGRATING) {
        const redirectTo = isSuperAdmin && !isImpersonating ? '/admin' : '/';
        return <Navigate to={redirectTo} replace />;
    }

    return <Outlet />;
};

export default GuestGuard;
